import * as React          from "react";
import ReactTooltip        from 'react-tooltip';
import {cx}                from "emotion";

import "./download-photos-modal.scss";

import {ComponentLink, Link, makeChain} from "@Common/react/cst2";
import {Load2}                          from "@Common/react/load2";
import {UseState}                       from "@Common/react/use-state";
import {downloadAsZip}                  from "@Common/tools/cc-integration/common/download-as-zip";
import {Types}                          from "@Common/types";
import {ModalService3}                  from "@Common/ui-components/modal-service-3/modal-service-3";
import {Responsive}                     from "@Common/ui-components/responsive";
import {SliderCheckbox}                 from "@Common/ui-components/slider-checkbox/slider-checkbox";
import {MEDIA_QUERY_MD, ViewportTest}   from "@Common/ui-components/viewport-test";
import {guessImgBinaryExtension}        from "@Common/utils/browser/guess-img-binary-extension";
import {bytesToUnitString}              from "@Common/utils/math/bytes-to-unit-string";

import {consumeContext} from "@Xpresso/loaders/consume-context";
import {ModalLayout}    from "@Xpresso/routes/common/modal-layout/modal-layout";

export const DownloadPhotosModal = ({
    next: rootNext,
    title,
    urlParam,
}: {
    next: ComponentLink<typeof ModalService3<{itemId: number}>, "rootNext">,
    title: string,
    urlParam: string,
}) => (
    makeChain<JSX.Element>()
    .then(...consumeContext("apis"))
    .then("downloading", ({}, next:ComponentLink<typeof UseState<boolean>>) => UseState({next}))
    .then("responsive", ({}, next:Link<JSX.Element, any>) => <Responsive {...{
        sizes: {
            mobile: 500,
        },
        next,
    }}/>)
    .then("modal", ({downloading, responsive}, next:ComponentLink<typeof ModalService3<{itemId: number}>>) => ModalService3({
        onPassiveClose: async () => downloading.onChange(false),
        width: responsive.lt("mobile") ? 287 : 780,
        xdQaName: "Download Photos Modal",
        next,
        urlParam,
        rootNext,
    }))
    .then("selectedPhotos", ({}, next:ComponentLink<typeof UseState<Types.Apis.Models.GalleryImagePreviousOrder[]>>) => UseState({
        next,
        initValue: []
    }))
    .then("photos", ({apis, modal}, next:ComponentLink<typeof Load2<Types.Apis.Payloads.Next.Gallery.PreviousOrdersGetImagesResponse>>) => Load2({
        _key: "images_" + modal.params.itemId,
        fetch: () => apis.next.gallery.previousOrders.getImages(modal.params.itemId),
        captureException: true,
        next,
    }))
    .then("isMobile", ({}, next:Link<React.ReactNode, boolean>) => (
        <ViewportTest if={MEDIA_QUERY_MD} then={() => next(true)} else={() => next(false)}/>
    ))
    .then(({downloading, isMobile, modal, photos, selectedPhotos}, next:Link<JSX.Element>) => ModalLayout({
        title,
        content: next(),
        headerContent: isMobile ? undefined : headerComments({selectedPhotos}),
        footerContent: isMobile ? undefined : selectAllToggle({photos, selectedPhotos}),
        buttons: [
            {
                label: "Cancel",
                onClick: () => {
                    selectedPhotos.onChange([]);
                    downloading.onChange(false);
                    modal.close();
                },
            },
            {
                label: (() => (downloading.value ? "Downloading..." : "Download Selected"))(),
                disabled: (() => (downloading.value || !selectedPhotos.value.length || totalSize(selectedPhotos.value) > 1073741824))(), //1 GB
                primary: true,
                onClick: async () => {
                    downloading.onChange(true);
                    await download({photos: selectedPhotos.value, itemId: modal.params.itemId, downloading});
                },
            },
        ],
    }))
    .then(({isMobile, photos, selectedPhotos}) => (
        <div className="download-photos-modal-p3e">
            {isMobile && <div>{headerComments({selectedPhotos})}</div>}
            <div className="photos-grid">
                {photos.loading ? (
                    <div className="loading" xd-qa-type="label" xd-qa-name="Loading">Loading...</div>
                ) : !photos.value ? (
                    <div className="loading" xd-qa-type="label" xd-qa-name="Empty">No Photos Available for this order</div>
                ) : (<>
                    <ReactTooltip  id="download-photos-modal" place="bottom" delayShow={100}/>
                    {photos.value.sort((a,b) => sortByName(a,b)).map((photo) => (
                        <div className="photo-card" key={photo.id} xd-qa-type="card" xd-qa-name={cx(photo.name, {selected: Boolean(selectedPhotos.value.find((selectedPhoto) => selectedPhoto.id === photo.id))})}>
                            <div
                                className={cx("img-container", {selected: Boolean(selectedPhotos.value.find((selectedPhoto) => selectedPhoto.id === photo.id))})}
                                onClick={() => selectedPhotos.value.find((selectedPhoto) => selectedPhoto.id === photo.id) ? (
                                    selectedPhotos.onChange(selectedPhotos.value.filter((selectedPhoto) => selectedPhoto.id !== photo.id))
                                ) : (
                                    selectedPhotos.onChange([...selectedPhotos.value, photo])
                                )}
                            >
                                <img src={photo.thumbnailUrl} alt={photo.name}/>
                                {selectedPhotos.value.find((selectedPhoto) => selectedPhoto.id === photo.id) && (
                                    <div className="check">
                                        <i className="fa fa-check"/>
                                    </div>
                                )}
                            </div>
                            <div className="image-name" data-for="download-photos-modal" data-tip={photo.name} xd-qa-type="text" xd-qa-name="Photo Name">{photo.name}</div>
                        </div>
                    ))}
                </>)}
            </div>
            {isMobile && <div>{selectAllToggle({photos, selectedPhotos})}</div>}
        </div>
    ))
    .run()
);

const sortByName = (a,b) => (
    (a.name.toLowerCase() > b.name.toLowerCase()) ? (
        1
    ) : (b.name.toLowerCase() > a.name.toLowerCase()) ? (
        -1
    ) : (
        0
    )
);

const selectAllToggle = ({photos, selectedPhotos}) => (
    <div className="download-photos-modal-toggle-c0r">
        {SliderCheckbox({
            value: selectedPhotos.value.length === photos?.value?.length,
            onChange: () => selectedPhotos.value.length === photos?.value?.length ? (
                selectedPhotos.onChange([])
            ) : (
                selectedPhotos.onChange(photos?.value)
            ),
            xdQaName: "Select All Photos",
        })}
        <div className="label" xd-qa-type="label" xd-qa-name="Select All Photos">
            Select All Photos
        </div>
        {totalSize(selectedPhotos.value) > 1073741824 && (
            <div className="error" xd-qa-type="label" xd-qa-name="Error">
                Total download size cannot exceed 1 GB
            </div>
        )}
    </div>
)

const headerComments = ({selectedPhotos}) => (
    selectedPhotos.value.length ? (
        <div className="download-photos-modal-header-c0r">
            <div className="label" xd-qa-type="label" xd-qa-name="Header Comment">
                {`${selectedPhotos.value.length} ${selectedPhotos.value.length > 1 ? "photos" : "photo"} will be downloaded (Total ${bytesToUnitString(totalSize(selectedPhotos.value), 2, true)})`}
            </div>
            <div className="link" xd-qa-type="link" xd-qa-name="Clear Selection" onClick={() => selectedPhotos.onChange([])}>
                Clear Selection
            </div>
        </div>
    ) : (
        <div className="download-photos-modal-header-c0r">
            <div className="label" xd-qa-type="label" xd-qa-name="Header Comment">
                Select the photos you want to download
            </div>
        </div>
    )
)

const totalSize = (arr) => {
    const sum = arr.reduce((accumulator, object) => {
        return accumulator + object.fileSize;
    }, 0)
    return sum;
};

//functions below similar to xpressdocs_next/ui/common/tools/cc-integration/watermark_download/watermark_download.tsx
export const download = async ({photos, itemId, downloading}) => {
    if (photos && Array.isArray(photos) && photos.length >= 1) {
        await downloadAsZip(
            photos.map( (photo) =>{
                return {
                    url: photo.printUrl,
                    getFilePath: (binary, {isExisted}) => {

                        const extension = guessImgBinaryExtension(binary) || photo.printUrl.split('.').pop().split(/#|\?/)[0];
                        let filename =  `${photo.name}.${extension}`;
                        let num = 1;
                        //check if file name exists and append number to avoid duplicates
                        while (isExisted(filename)) {
                            filename = `${photo.name}${num}.${extension}`;
                            num++;
                        }
                        return filename;
                    },
                };
            }),
            {fileName: `${itemId}_Photos.zip`},
        );
        downloading.onChange(false);
    }
};

